import { Shield, User, FileCheck, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const EmployeeGulfSection = ({ formData, updateField, countryCode }) => {
  const { t } = useTranslation();
  const isQA = countryCode === "QA";
  const isSA = countryCode === "SA";
  if (!isQA && !isSA) return null;

  const isExpatriate = formData.is_expatriate === true;

  return (
    <div className="space-y-4">
      <h3 className="font-medium flex items-center space-x-2">
        <Shield className="h-4 w-4" />
        <span>
          {isQA
            ? t("employeesPage.gulf.qatarSection")
            : t("employeesPage.gulf.saudiSection")}
        </span>
      </h3>

      {/* QID — required for QA, optional for SA */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="qid">
            {isQA ? t("employeesPage.gulf.qidLabel") : t("employeesPage.gulf.nationalIdLabel")}
            {isQA && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            id="qid"
            placeholder={isQA ? "2XXXXXXXXXX or 3XXXXXXXXXX" : ""}
            value={formData.qid ?? ""}
            onChange={(e) => updateField("qid", e.target.value)}
            maxLength={11}
          />
          {isQA && formData.qid && !/^[23]\d{10}$/.test(formData.qid) && (
            <p className="text-xs text-red-500">{t("employeesPage.gulf.qidError")}</p>
          )}
        </div>

        {/* Expatriate toggle */}
        <div className="space-y-2 flex flex-col justify-center">
          <Label className="flex items-center gap-2 cursor-pointer select-none">
            <Switch
              id="is_expatriate"
              checked={isExpatriate}
              onCheckedChange={(checked) => updateField("is_expatriate", checked)}
            />
            <span>{t("employeesPage.gulf.expatriateLabel")}</span>
          </Label>
          <p className="text-xs text-muted-foreground">
            {isExpatriate
              ? t("employeesPage.gulf.expatriateDesc")
              : t("employeesPage.gulf.nationalDesc")}
          </p>
        </div>
      </div>

      {/* National fields */}
      {!isExpatriate && (
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gosi_number">
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {t("employeesPage.gulf.gosiLabel")}
              </span>
            </Label>
            <Input
              id="gosi_number"
              placeholder={t("employeesPage.gulf.gosiPlaceholder")}
              value={formData.gosi_number ?? ""}
              onChange={(e) => updateField("gosi_number", e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Expatriate fields */}
      {isExpatriate && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="iqama_number">
                <span className="flex items-center gap-1">
                  <FileCheck className="h-3.5 w-3.5" />
                  {t("employeesPage.gulf.iqamaLabel")}
                </span>
              </Label>
              <Input
                id="iqama_number"
                placeholder={t("employeesPage.gulf.iqamaPlaceholder")}
                value={formData.iqama_number ?? ""}
                onChange={(e) => updateField("iqama_number", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="work_permit_number">{t("employeesPage.gulf.workPermitLabel")}</Label>
              <Input
                id="work_permit_number"
                placeholder={t("employeesPage.gulf.workPermitLabel")}
                value={formData.work_permit_number ?? ""}
                onChange={(e) => updateField("work_permit_number", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="work_permit_expiry">{t("employeesPage.gulf.workPermitExpiry")}</Label>
              <Input
                id="work_permit_expiry"
                type="date"
                value={formData.work_permit_expiry ?? ""}
                onChange={(e) => updateField("work_permit_expiry", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="visa_residence_number">{t("employeesPage.gulf.residenceVisaLabel")}</Label>
              <Input
                id="visa_residence_number"
                placeholder={t("employeesPage.gulf.residenceVisaLabel")}
                value={formData.visa_residence_number ?? ""}
                onChange={(e) => updateField("visa_residence_number", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visa_residence_expiry">{t("employeesPage.gulf.residenceVisaExpiry")}</Label>
              <Input
                id="visa_residence_expiry"
                type="date"
                value={formData.visa_residence_expiry ?? ""}
                onChange={(e) => updateField("visa_residence_expiry", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="visa_work_number">{t("employeesPage.gulf.workVisaLabel")}</Label>
              <Input
                id="visa_work_number"
                placeholder={t("employeesPage.gulf.workVisaLabel")}
                value={formData.visa_work_number ?? ""}
                onChange={(e) => updateField("visa_work_number", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visa_work_expiry">{t("employeesPage.gulf.workVisaExpiry")}</Label>
              <Input
                id="visa_work_expiry"
                type="date"
                value={formData.visa_work_expiry ?? ""}
                onChange={(e) => updateField("visa_work_expiry", e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      {/* Salary structure — drives the GOSI/QPSA contributory base (base + housing) */}
      <div className="pt-2 border-t border-border/60 space-y-3">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Wallet className="h-3.5 w-3.5" />
          {t("employeesPage.gulf.salaryStructureTitle")}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t("employeesPage.gulf.salaryStructureHint")}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="basic_salary">{t("employeesPage.gulf.basicSalaryLabel")}</Label>
            <Input
              id="basic_salary"
              type="number"
              min="0"
              step="0.01"
              value={formData.basic_salary ?? ""}
              onChange={(e) => updateField("basic_salary", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="housing_allowance">{t("employeesPage.gulf.housingAllowanceLabel")}</Label>
            <Input
              id="housing_allowance"
              type="number"
              min="0"
              step="0.01"
              value={formData.housing_allowance ?? ""}
              onChange={(e) => updateField("housing_allowance", e.target.value)}
            />
          </div>
          {isQA && (
            <div className="space-y-2">
              <Label htmlFor="social_allowance">{t("employeesPage.gulf.socialAllowanceLabel")}</Label>
              <Input
                id="social_allowance"
                type="number"
                min="0"
                step="0.01"
                value={formData.social_allowance ?? ""}
                onChange={(e) => updateField("social_allowance", e.target.value)}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="transport_allowance">{t("employeesPage.gulf.transportAllowanceLabel")}</Label>
            <Input
              id="transport_allowance"
              type="number"
              min="0"
              step="0.01"
              value={formData.transport_allowance ?? ""}
              onChange={(e) => updateField("transport_allowance", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="other_allowances">{t("employeesPage.gulf.otherAllowancesLabel")}</Label>
            <Input
              id="other_allowances"
              type="number"
              min="0"
              step="0.01"
              value={formData.other_allowances ?? ""}
              onChange={(e) => updateField("other_allowances", e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeGulfSection;
